// ============================================================================
// localStorage persistence + migration from the original v0 single-file shape.
// ============================================================================

import type { PersistedLearner, Progress } from './types.js';

export const SCHEMA_VERSION = 1;

export function emptyProgress(): Progress {
  return {
    completedModules: [],
    moduleScores: {},
    feedbackSubmitted: {},
    moduleTimeSeconds: {},
    quizAttempts: {},
    reviewRequired: {},
    startedAt: null,
    lastActive: null,
    completedAt: {},
  };
}

export function emptyLearner(): PersistedLearner {
  return {
    schemaVersion: SCHEMA_VERSION,
    name: '',
    progress: emptyProgress(),
  };
}

/**
 * Migrate a legacy v0 (untyped, single-file) record to the current shape.
 * v0 had no schemaVersion, no lastActive, no completedAt, and some learners
 * may have quizAttempts without matching reviewRequired entries.
 */
export function migrate(raw: unknown): PersistedLearner {
  if (raw === null || typeof raw !== 'object') return emptyLearner();
  const obj = raw as Record<string, unknown>;
  const name = typeof obj['name'] === 'string' ? (obj['name'] as string) : '';

  const progIn =
    typeof obj['progress'] === 'object' && obj['progress'] !== null
      ? (obj['progress'] as Record<string, unknown>)
      : {};

  const progress: Progress = {
    ...emptyProgress(),
    ...{
      completedModules: Array.isArray(progIn['completedModules'])
        ? (progIn['completedModules'] as string[])
        : [],
      moduleScores:
        typeof progIn['moduleScores'] === 'object' && progIn['moduleScores']
          ? (progIn['moduleScores'] as Record<string, number>)
          : {},
      feedbackSubmitted:
        typeof progIn['feedbackSubmitted'] === 'object' &&
        progIn['feedbackSubmitted']
          ? (progIn['feedbackSubmitted'] as Record<string, boolean>)
          : {},
      moduleTimeSeconds:
        typeof progIn['moduleTimeSeconds'] === 'object' &&
        progIn['moduleTimeSeconds']
          ? (progIn['moduleTimeSeconds'] as Record<string, number>)
          : {},
      quizAttempts:
        typeof progIn['quizAttempts'] === 'object' && progIn['quizAttempts']
          ? (progIn['quizAttempts'] as Record<string, number>)
          : {},
      reviewRequired:
        typeof progIn['reviewRequired'] === 'object' && progIn['reviewRequired']
          ? (progIn['reviewRequired'] as Record<string, boolean>)
          : {},
      startedAt:
        typeof progIn['startedAt'] === 'string'
          ? (progIn['startedAt'] as string)
          : null,
      lastActive:
        typeof progIn['lastActive'] === 'string'
          ? (progIn['lastActive'] as string)
          : null,
      completedAt:
        typeof progIn['completedAt'] === 'object' && progIn['completedAt']
          ? (progIn['completedAt'] as Record<string, string>)
          : {},
    },
  };

  return { schemaVersion: SCHEMA_VERSION, name, progress };
}

export interface PersistenceAdapter {
  load(): PersistedLearner;
  save(record: PersistedLearner): void;
  clear(): void;
}

export function localStorageAdapter(
  storageKey: string,
  storage: Storage = globalThis.localStorage,
): PersistenceAdapter {
  return {
    load() {
      try {
        const raw = storage.getItem(storageKey);
        if (!raw) return emptyLearner();
        return migrate(JSON.parse(raw));
      } catch {
        return emptyLearner();
      }
    },
    save(record) {
      record.progress.lastActive = new Date().toISOString();
      storage.setItem(storageKey, JSON.stringify(record));
    },
    clear() {
      storage.removeItem(storageKey);
    },
  };
}
